import { store } from "../store/store";
import { trackClick, sendClickBatch } from "../store/thunks/analyticsThunk";

// Global click listener state
let isInitialized = false;
let clickSequence = 0;
let pageLoadTime = Date.now();
let batchTimer = null;

/**
 * Generate XPath for an element
 * @param {HTMLElement} element - The DOM element
 * @returns {string} - XPath string
 */
function getXPath(element) {
  if (!element) return "";

  // Use ID if available (faster and more reliable)
  if (element.id) {
    return `id("${element.id}")`;
  }

  // If we've reached the body, return the path
  if (element === document.body) {
    return "/html/body";
  }

  // Build path recursively
  let ix = 0;
  const siblings = element.parentNode?.childNodes || [];

  for (let i = 0; i < siblings.length; i++) {
    const sibling = siblings[i];

    if (sibling === element) {
      const parentPath = getXPath(element.parentNode);
      const tagName = element.tagName.toLowerCase();
      return `${parentPath}/${tagName}[${ix + 1}]`;
    }

    if (sibling.nodeType === 1 && sibling.tagName === element.tagName) {
      ix++;
    }
  }

  return "";
}

/**
 * Get React component name from element using React Fiber
 * @param {HTMLElement} element - The DOM element
 * @returns {string|null} - Component name or null
 */
function getComponentName(element) {
  let current = element;
  const maxDepth = 15; // Prevent infinite loops
  let depth = 0;

  while (current && current !== document.body && depth < maxDepth) {
    depth++;

    // Method 1: Check for explicit data-component attribute
    if (current.dataset && current.dataset.component) {
      return current.dataset.component;
    }

    // Method 2: Use React Fiber to get actual component name
    // React attaches internal instance to DOM nodes with key starting with __react
    const fiberKey = Object.keys(current).find(
      key => key.startsWith('__reactFiber') || key.startsWith('__reactInternalInstance')
    );

    if (fiberKey) {
      let fiber = current[fiberKey];

      // Walk up the fiber tree to find component name
      while (fiber) {
        // Function components and class components have different structures
        const componentName =
          fiber.type?.name || // Function component
          fiber.type?.displayName || // Component with displayName
          fiber.elementType?.name || // Class component
          fiber.elementType?.displayName;

        if (componentName &&
            !componentName.startsWith('_') &&
            componentName !== 'Unknown' &&
            // Filter out generic HTML elements and common low-level components
            !['div', 'span', 'button', 'input', 'form', 'a', 'Fragment', 'Profiler'].includes(componentName)) {
          return componentName;
        }

        fiber = fiber.return;
      }
    }

    // Method 3: Check for common React class patterns (fallback)
    if (current.className) {
      const classes = current.className.toString().split(" ");
      for (const cls of classes) {
        if (cls.includes("Modal") || cls.includes("Page") || cls.includes("Dashboard")) {
          return cls;
        }
      }
    }

    current = current.parentElement;
  }

  return null;
}

/**
 * Check if click should be tracked
 * @param {HTMLElement} element - The clicked element
 * @returns {boolean}
 */
function shouldTrackClick(element) {
  // Don't track clicks on certain elements
  const ignoredTags = ["HTML", "BODY"];
  if (ignoredTags.includes(element.tagName)) {
    return false;
  }

  // Don't track clicks with data-no-analytics attribute
  if (element.dataset && element.dataset.noAnalytics === "true") {
    return false;
  }

  // Don't track clicks in development tools
  if (element.closest && element.closest("[data-analytics-ignore]")) {
    return false;
  }

  return true;
}

/**
 * Handle click event
 * @param {MouseEvent} event - The click event
 */
function handleClick(event) {
  try {
    const target = event.target;

    // Check if click should be tracked
    if (!shouldTrackClick(target)) {
      return;
    }

    // Get current auth state
    const state = store.getState();
    const user = state.auth?.user;

    // Don't track if not authenticated
    if (!user || !user._id) {
      return;
    }

    // Increment click sequence
    clickSequence++;

    // Calculate time on page
    const timeOnPage = Date.now() - pageLoadTime;

    // Get viewport dimensions
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Build click data
    const clickData = {
      // Element details
      elementType: target.tagName,
      elementId: target.id || null,
      elementClass: (typeof target.className === 'string' ? target.className : target.className?.baseVal) || null,
      elementText: target.textContent?.substring(0, 50) || null,
      xpath: getXPath(target),

      // Component context
      componentName: getComponentName(target),

      // Page context
      path: window.location.pathname,

      // Timing
      timestamp: Date.now(),
      pageLoadTime: pageLoadTime,
      timeOnPage: timeOnPage,
      clickSequence: clickSequence,

      // Browser/device info
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: viewport,
    };

    // Dispatch to Redux
    store.dispatch(trackClick(clickData));

    // Check if we should send batch
    const analyticsState = store.getState().analytics;
    const batchSize = analyticsState?.batchSize || 10;
    const currentBatchLength = analyticsState?.clickBatch?.length || 0;

    if (currentBatchLength >= batchSize) {
      sendBatchNow();
    }
  } catch (error) {
    console.error("[Global Click Listener] Error tracking click:", error);
  }
}

/**
 * Send batch immediately and reset timer
 */
function sendBatchNow() {
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }

  store.dispatch(sendClickBatch());
  resetBatchTimer();
}

/**
 * Reset batch timer
 */
function resetBatchTimer() {
  if (batchTimer) {
    clearTimeout(batchTimer);
  }

  const analyticsState = store.getState().analytics;
  const batchTimeout = analyticsState?.batchTimeout || 30000;

  batchTimer = setTimeout(() => {
    const currentBatchLength = store.getState().analytics?.clickBatch?.length || 0;

    if (currentBatchLength > 0) {
      console.log(`[Global Click Listener] Batch timeout reached. Sending ${currentBatchLength} clicks.`);
      store.dispatch(sendClickBatch());
    }

    resetBatchTimer();
  }, batchTimeout);
}

/**
 * Handle page visibility change (send batch before page unload)
 */
function handleVisibilityChange() {
  if (document.visibilityState === "hidden") {
    const currentBatchLength = store.getState().analytics?.clickBatch?.length || 0;

    if (currentBatchLength > 0) {
      console.log(`[Global Click Listener] Page hidden. Sending ${currentBatchLength} clicks.`);
      store.dispatch(sendClickBatch());
    }
  }
}

/**
 * Handle before unload (send batch before page closes)
 */
function handleBeforeUnload() {
  const currentBatchLength = store.getState().analytics?.clickBatch?.length || 0;

  if (currentBatchLength > 0) {
    console.log(`[Global Click Listener] Page unloading. Sending ${currentBatchLength} clicks.`);
    // Use navigator.sendBeacon for reliable sending on page unload
    const state = store.getState();
    const batch = state.analytics.clickBatch;

    if (batch.length > 0) {
      const baseURL = process.env.REACT_APP_BASE_URL || "";
      const token = state.auth.token;

      // Use sendBeacon for reliability
      const data = JSON.stringify({ clicks: batch });
      const blob = new Blob([data], { type: "application/json" });

      navigator.sendBeacon(`${baseURL}/api/analytics/clicks/batch`, blob);
    }
  }
}

/**
 * Reset page tracking on route change
 */
function handleRouteChange() {
  pageLoadTime = Date.now();
  clickSequence = 0;
  console.log(`[Global Click Listener] Route changed to ${window.location.pathname}. Reset tracking.`);
}

/**
 * Initialize global click tracking
 */
export function initializeClickTracking() {
  if (isInitialized) {
    console.warn("[Global Click Listener] Already initialized");
    return;
  }

  console.log("[Global Click Listener] Initializing component-based click analytics with React Fiber detection...");

  // Add click listener in capture phase to catch all clicks
  document.addEventListener("click", handleClick, true);

  // Add visibility change listener
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Add before unload listener
  window.addEventListener("beforeunload", handleBeforeUnload);

  // Listen for route changes (for SPAs)
  window.addEventListener("popstate", handleRouteChange);

  // Start batch timer
  resetBatchTimer();

  isInitialized = true;
  console.log("[Global Click Listener] Component-based click analytics initialized successfully");
}

/**
 * Cleanup click tracking
 */
export function cleanupClickTracking() {
  if (!isInitialized) {
    return;
  }

  console.log("[Global Click Listener] Cleaning up click analytics...");

  // Send any remaining clicks
  const currentBatchLength = store.getState().analytics?.clickBatch?.length || 0;
  if (currentBatchLength > 0) {
    store.dispatch(sendClickBatch());
  }

  // Remove listeners
  document.removeEventListener("click", handleClick, true);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("beforeunload", handleBeforeUnload);
  window.removeEventListener("popstate", handleRouteChange);

  // Clear timer
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }

  isInitialized = false;
  console.log("[Global Click Listener] Click analytics cleaned up");
}

/**
 * Check if tracking is initialized
 */
export function isTrackingInitialized() {
  return isInitialized;
}

export default {
  initializeClickTracking,
  cleanupClickTracking,
  isTrackingInitialized,
};
